import { VeboxModels } from "../../../../../types/veboxModels";
import { TopHierarchyNode, TreeNode } from "./types";
import { VeboxTreeView } from "../../../../../components/tree_view/VeboxTreeView";
import * as React from "react";
import { VeBoxEquipmentFilters } from "../../data/models/VeBoxEquipmentFilters";
import { commercialHierarchyGUID } from "../../../../user_creation/stages/Visibility";
import { hierarchyIncludesWord } from "../../../../../users/HierarchyVisibilityUtils";

export const findNodeInHierarchy = (
	node: VeboxModels.HierarchiesTree,
	nodeGuid: string,
): VeboxModels.HierarchiesTree | undefined => {
	if (node.hierarchyGuid === nodeGuid) {
		return node;
	}
	if (node.children) {
		for (const child of node.children) {
			const foundNode = findNodeInHierarchy(child, nodeGuid);
			if (foundNode) {
				return foundNode;
			}
		}
	}
	return undefined;
};

export const findNodeInHierarchies = (
	hierarchy: VeboxModels.HierarchiesTree[],
	nodeGuid: string,
): TopHierarchyNode | undefined => {
	for (const parentHierarchy of hierarchy) {
		const foundNode = findNodeInHierarchy(parentHierarchy, nodeGuid);
		if (foundNode) {
			return {
				hierarchyGuid: parentHierarchy.hierarchyGuid,
				nodes: [{ nodeGuid: foundNode.hierarchyGuid, name: foundNode.label }],
			};
		}
	}
	return undefined;
};

const mergeSelectedHierarchyNodes = (
	selectedHierarchyNodes: TopHierarchyNode[],
	nodeToAdd: TopHierarchyNode,
) => {
	const newList = [...selectedHierarchyNodes];
	const hierarchyIndex = newList.findIndex(
		(node) => node.hierarchyGuid === nodeToAdd.hierarchyGuid,
	);
	if (hierarchyIndex !== -1) {
		newList[hierarchyIndex].nodes.push(...nodeToAdd.nodes);
	} else {
		newList.push(nodeToAdd);
	}
	return newList;
};

export const getHierarchyFilterInfo = (
	hierarchyTree: VeboxModels.HierarchiesTree[],
	nodes: (string | TreeNode)[],
): TopHierarchyNode[] => {
	let selectedHierarchyNodes: TopHierarchyNode[] = [];
	for (const node of nodes) {
		if (typeof node === "string" || node.checked) {
			const nodeGuid = typeof node === "string" ? node : node.value;
			const foundNode = findNodeInHierarchies(hierarchyTree, nodeGuid);
			if (foundNode) {
				selectedHierarchyNodes = mergeSelectedHierarchyNodes(
					selectedHierarchyNodes,
					foundNode,
				);
			}
		} else {
			selectedHierarchyNodes.push(
				...getHierarchyFilterInfo(hierarchyTree, node.children),
			);
		}
	}
	return selectedHierarchyNodes;
};

export const filteredHierarchiesTree = (
	tree: VeboxModels.HierarchiesTree,
	searchWord: string,
): VeboxModels.HierarchiesTree => {
	const newChildren = tree.children
		.map((child) => filteredHierarchiesTree(child, searchWord))
		.filter(
			(child) =>
				child.label.toLowerCase().includes(searchWord.toLowerCase()) ||
				child.children.length > 0,
		);
	return {
		...tree,
		children: newChildren,
	};
};

export const buildHierarchiesTree = (
	tree: VeboxModels.HierarchiesTree,
	searchWord: string,
) => {
	const hierarchyIncludesSearchWord: boolean =
		searchWord !== "" &&
		(tree.label.includes(searchWord) ||
			(tree.children ?? []).filter(hierarchyIncludesWord(searchWord)).length >
				0);

	return (
		<VeboxTreeView.Checkbox
			className={`sites-tree-node`}
			label={<span className={`label`}>{tree.label}</span>}
			key={tree.hierarchyGuid}
			value={tree.hierarchyGuid}
			expanded={hierarchyIncludesSearchWord}
			style={{
				display: searchWord === "" || hierarchyIncludesSearchWord ? "" : "none",
			}}
		>
			{(tree.children ?? []).map((subTree) => {
				return buildHierarchiesTree(subTree, searchWord);
			})}
		</VeboxTreeView.Checkbox>
	);
};

export const prepareHierarchyFilters = (
	hierarchyNodeForFilter: TopHierarchyNode[],
): VeBoxEquipmentFilters => {
	const commercialHierarchy = hierarchyNodeForFilter.find(
		(node) => node.hierarchyGuid === commercialHierarchyGUID,
	);
	if (commercialHierarchy) {
		return {
			saleFiltersCategory: {
				commercialHierarchySearch: commercialHierarchy,
			},
		};
	}
	return {
		companyFiltersCategory: {
			hierarchySearches: hierarchyNodeForFilter.filter(
				(node) => node.hierarchyGuid !== commercialHierarchyGUID,
			),
		},
	};
};

export const isCommercialHierarchyNode = (node: TopHierarchyNode): boolean => {
	return node.hierarchyGuid === commercialHierarchyGUID;
};
